<template>
	<section id="key-features" class="key-features">
		<div class="container">
			<h2 class="key-features-heading text-upper text-center">Key features at a glance</h2>
			<div v-if="totalBuilds > 1" class="model-switcher">
				<div
					v-for="(build, index) in builds"
					:key="`model-switcher-${build.id}`"
					:class="{ 'selected': build.id == currentBuild.id, 'disable-element': isUpdating || build.id == currentBuild.id }"
					class="button"
					@click="updateModel(index)"
				>
					{{ build.description }}
				</div>
			</div>
			<div class="row features-container" v-if="parts.cpus">
				<div class="col-md-3 col-sm-6">
					<div class="individual-feature text-center">
						<div class="feature-icon">
							<img src="images/icons/key-features-icons/cpu.svg" alt="CPU Icon" class="m-auto">
						</div>
						<div class="feature-text">
							<h4 class="feature-text-heading">CPU</h4>
							<p class="feature-text-sub">{{ parts.cpus.title }}</p>
						</div>
					</div>
				</div>
				<div class="col-md-3 col-sm-6">
					<div
						v-show="buildsIndex"
						class="upgrade"
					>
						Upgraded
					</div>
					<div class="individual-feature text-center graphics">
						<div class="feature-icon">
							<img src="images/icons/key-features-icons/videocard-gpu.svg" alt="GPU Icon" class="m-auto">
						</div>
						<div class="feature-text">
							<div>
								<h4 class="feature-text-heading">Graphics</h4>
								<div
									v-show="buildsIndex"
									class="upgrade-mobile"
								>
									Upgraded
								</div>
							</div>
							<p class="feature-text-sub">{{ parts.gpus.title }}</p>
						</div>
					</div>
				</div>
				<div class="col-md-3 col-sm-6">
					<div class="individual-feature text-center">
						<div class="feature-icon">
							<img src="images/icons/key-features-icons/ram.svg" alt="RAM Memory Icon" class="m-auto">
						</div>
						<div class="feature-text">
							<h4 class="feature-text-heading">RAM</h4>
							<p class="feature-text-sub">
								{{ parts.ram.name }}
								<small>(max speed)</small>
								<span v-html="computeRamCapacity(parts.ram.capacity, parts.ram.slots, parts.ram.quantity)" />
							</p>
						</div>
					</div>
				</div>
				<div class="col-md-3 col-sm-6">
					<div
						v-show="buildsIndex"
						class="upgrade"
					>
						Upgraded
					</div>
					<div class="individual-feature text-center storage">
						<div class="feature-icon">
							<img src="images/icons/key-features-icons/storage-ssd.svg" alt="SSD Icon" class="m-auto">
						</div>
						<div class="feature-text">
							<div>
								<h4 class="feature-text-heading">Storage</h4>
								<div
									v-show="buildsIndex"
									class="upgrade-mobile"
								>
									Upgraded
								</div>
							</div>
							<p class="feature-text-sub">
								<template v-for="ssd in parts.storage.SSD">
									{{ $route.name != 'creator-pc' ? ssd.title : ssd.name }}
									{{ ssd.capacity }}
									<br :key="`ssd-${ssd.id}`">
								</template>
								<template v-if="parts.storage.HDD">
									<template v-for="hdd in parts.storage.HDD">
										{{ $route.name != 'creator-pc' ? hdd.title : hdd.name }}
										{{ hdd.capacity }}
										<br :key="`hdd-${hdd.id}`">
									</template>
								</template>
							</p>
						</div>
					</div>
				</div>
			</div>
			
			<icon-feature />

			<transition name="slide-in">
				<pc-specs v-show="showSpecs" :parts="parts" :upgrade="Boolean(buildsIndex)" />
			</transition>

			<button
				class="btn btn-purple text-uppercase"
				:class="{'slide-down-button': showSpecs, 'slide-up-button': !showSpecs}"
				@click="showSpecs = !showSpecs"
			>
				{{ showSpecs ? "Hide" : "Show" }} Specs
			</button>
		</div>
	</section>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { computeRamCapacity } from "@u/store-helpers";
import PcSpecs from "./pc-specs";
import IconFeature from "./icon-features";

export default {
	name: "KeyFeatures",
	components: {
		PcSpecs,
		IconFeature
	},
	data() {
		return {
			showSpecs: false
		};
	},
	computed: {
		...mapState({
			builds: state => state.StarterPc.builds,
			buildsIndex: state => state.StarterPc.buildsIndex,
			currentBuild: state => state.StarterPc.builds[state.StarterPc.buildsIndex],
			isUpdating: state => state.StarterPc.isUpdating
		}),
		...mapGetters({
			totalBuilds: "StarterPc/totalBuilds"
		}),
		parts() {
			return this.currentBuild.parts;
		},
		ramCapacity() {
			return this.getRealCapacity(this.parts.ram);
		},
		ramQuantity() {
			return Number.parseInt(this.parts.ram.quantity) * Math.abs(this.parts.ram.slots);
		},
		isRgbActive() {
			const activeRgbRoute = ["streaming-pc", "creator-pc", "h1-mini-pc"];
			return activeRgbRoute.includes(this.$route.name);
		},
		isUsbCActive() {
			const activeUsbCRoutes = ["creator-pc", "h1-mini-pc"];
			return activeUsbCRoutes.includes(this.$route.name);
		}
	},
	created() {
		EventBus.$on("open-specs", () => {
			this.showSpecs = true;
		});
	},
	methods: {
		computeRamCapacity,
		...mapActions({
			updateModel: "StarterPc/updateModel"
		})
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.slide-in-enter-active {
	animation: slide-in-top 1s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
.slide-in-leave-active {
	animation: slide-out-top 0.6s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}

@keyframes slide-in-top {
	0% {
		opacity: 0;
		max-height: 0;
	}

	100% {
		opacity: 1;
		max-height: 900px;
	}
}

@keyframes slide-out-top {
	0% {
		opacity: 1;
		max-height: 900px;
	}
	100% {
		opacity: 0;
		max-height: 0;
	}
}

.key-features {
	@media (min-width: 1200px) {
		.container {
			width: 1215px;
		}
	}

	background-color: $white;
	padding-bottom: 50px;

	.model-switcher {
		display: flex;
		justify-content: center;
		margin-bottom: 48px;

		.button {
			border: 1px solid $purple;
			color: $purple;
			cursor: pointer;
			padding: 6px 25px;
			text-align: center;
			width: 152px;

			&:first-child {
				border-radius: 3px 0 0 3px;
			}

			&:last-child {
				border-radius: 0 3px 3px 0;
			}

			&:hover,
			&.selected {
				background-color: $purple;
				color: $white;
			}
		}
	}

	.btn-purple {
		display: block;
		margin: 0 auto;
		background: $purple;
		border-color: $purple;

		&:hover {
			background: transparent;
			color: $purple;
		}

		&:focus {
			color: $white;

			&:hover {
				color: $purple;
			}
		}
	}

	&-heading {
		margin: 46px 0 32px;
		font-size: 36px;

		@media (max-width: 580px) {
			text-align: left;
			margin: 35px 0 10px;
		}
	}

	.features-container {
		.col-md-3,
		.col-sm-6 {
			padding: 0;
		}

		.upgrade-mobile,
		.upgrade {
			color: $orchid;
			font-size: 14px;
			text-transform: uppercase;
			letter-spacing: 1px;
		}

		.upgrade {
			position: absolute;
			right: 16px;
			top: 0;

			@media (max-width: 580px) {
				display: none;
			}

			span {
				font-size: 20px;
				vertical-align: top;
			}
		}

		.upgrade-mobile {
			@media (min-width: 581px) {
				display: none;
			}
		}

		.individual-feature {
			padding: 32px 15px 0;
			display: flex;
			align-items: center;
			flex-direction: column;
			height: 280px;
			border-right: 1px solid $light-grey;
			border-bottom: 1px solid $light-grey;

			@media (max-width: 580px) {
				padding: 0 0 0 16px;
				justify-content: start;
				flex-direction: row;
				height: 104px;
				margin: 0 15px;
				border-right: none;
			}

			.feature-icon {
				width: 128px;
				height: 128px;
				margin-bottom: 22px;
				display: flex;
				justify-content: center;
				flex-shrink: 0;

				@media (max-width: 580px) {
					width: 72px;
					height: 72px;
					margin: 0 16px 0 0;
				}

				img {
					width: 100%;
				}
			}

			.feature-text {
				@media (max-width: 580px) {
					text-align: left;
					width: 100%;

					& > div {
						display: flex;
						justify-content: space-between;
						align-items: flex-end;
					}
				}

				&-heading {
					margin-bottom: 0;
					color: $purple;
					font-size: 22px;
					font-weight: bold;
				}

				&-sub {
					color: #5f707f;
					font-size: 15px;
					margin-top: 3px;

					@media (max-width: 580px) {
						font-size: 14px;
					}
				}
			}
		}

		.storage {
			border-right: none !important;
		}

		.graphics {
			@media (max-width: 991px) {
				border-right: none;
			}
		}
	}
}
</style>
